import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useChat } from '../contexts/ChatContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { MessageSquare, Send, Loader2, User, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { trackContactCompany } from '../utils/analytics';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Messages = () => {
  const { user, token, isAuthenticated } = useAuth();
  const { fetchUnreadCount } = useChat();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetUserId = searchParams.get('user');
  
  const [conversations, setConversations] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newConversationUser, setNewConversationUser] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchConversations();
    markAllAsRead();
  }, [isAuthenticated, navigate, token]);

  const markAllAsRead = async () => {
    try {
      const res = await fetch(`${API}/messages/mark-all-read`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        // Odśwież licznik nieprzeczytanych wiadomości w Navbar
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  };

  // Obsługa parametru ?user=ID dla nowej konwersacji
  useEffect(() => {
    if (targetUserId && token && !loading) {
      // Sprawdź czy mamy już konwersację z tym użytkownikiem
      const existingConv = conversations.find(c => c.partner_id === targetUserId);
      if (existingConv) {
        selectPartner(existingConv.partner_id, existingConv.partner_name);
      } else {
        // Pobierz dane użytkownika i przygotuj nową konwersację
        fetchTargetUser(targetUserId);
      }
    }
  }, [targetUserId, conversations, token, loading]);

  const fetchTargetUser = async (userId) => {
    try {
      const res = await fetch(`${API}/users/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const userData = await res.json();
        setNewConversationUser(userData);
        setSelectedPartner({
          id: userId,
          name: userData.name || userData.company_name || 'Użytkownik',
          user_type: userData.user_type,
          isNew: true
        });
        setMessages([]);
      }
    } catch (error) {
      console.error('Error fetching target user:', error);
    }
  };

  const fetchConversations = async () => {
    try {
      const res = await fetch(`${API}/messages/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        // Pobierz user_type dla każdego partnera
        const enrichedConvs = await Promise.all(data.map(async (conv) => {
          try {
            const userRes = await fetch(`${API}/users/${conv.partner_id}`, {
              headers: { 'Authorization': `Bearer ${token}` },
              credentials: 'include',
            });
            if (userRes.ok) {
              const userData = await userRes.json();
              return {
                ...conv,
                partner_name: userData.name || userData.company_name || conv.partner_name,
                user_type: userData.user_type
              };
            }
          } catch (e) {
            console.error('Error enriching conversation:', e);
          }
          return conv;
        }));
        setConversations(enrichedConvs);
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (partnerId) => {
    try {
      const res = await fetch(`${API}/messages/with/${partnerId}`, {
        headers: { 'Authorization': `Bearer ${token}` },
        credentials: 'include',
      });
      if (res.ok) {
        setMessages(await res.json());
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const selectPartner = async (partnerId, partnerName, userType) => {
    setSelectedPartner({
      id: partnerId,
      name: partnerName,
      user_type: userType,
      isNew: false
    });
    await fetchMessages(partnerId);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedPartner) return;
    
    setSending(true);
    try {
      const res = await fetch(`${API}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ receiver_id: selectedPartner.id, content: newMessage }),
      });
      if (res.ok) {
        setNewMessage('');
        toast.success('Wiadomość wysłana!');
        
        // Śledź konwersję kontaktu w Google Ads (tylko pierwsza wiadomość w konwersacji)
        if (selectedPartner.isNew || messages.length === 0) {
          trackContactCompany(selectedPartner.id);
        }
        
        // Odśwież wiadomości i konwersacje
        await fetchMessages(selectedPartner.id);
        await fetchConversations();
        
        // Jeśli to była nowa konwersacja, oznacz jako nie-nowa
        if (selectedPartner.isNew) {
          setSelectedPartner(prev => ({ ...prev, isNew: false }));
          setNewConversationUser(null);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Nie udało się wysłać wiadomości');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Błąd wysyłania wiadomości');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="messages-page">
      <div className="bg-slate-900 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading font-bold text-2xl text-white">Wiadomości</h1>
          <p className="text-slate-400 mt-1">Komunikacja z firmami i klientami</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 h-[600px]">
          {/* Conversations List */}
          <Card className="md:col-span-1 overflow-hidden">
            <CardHeader className="border-b">
              <CardTitle className="font-heading text-lg">Konwersacje</CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-y-auto max-h-[500px]">
              {conversations.length === 0 && !newConversationUser ? (
                <div className="p-6 text-center text-slate-500">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>Brak wiadomości</p>
                </div>
              ) : (
                <div className="divide-y">
                  {/* Nowa konwersacja na górze */}
                  {newConversationUser && (
                    <button
                      onClick={() => {
                        setSelectedPartner({
                          id: targetUserId,
                          name: newConversationUser.name || newConversationUser.company_name || 'Użytkownik',
                          user_type: newConversationUser.user_type,
                          isNew: true
                        });
                        setMessages([]);
                      }}
                      className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${selectedPartner?.id === targetUserId && selectedPartner?.isNew ? 'bg-orange-50 border-l-4 border-orange-500' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                          {newConversationUser.user_type === 'company' ? <Building2 className="w-5 h-5 text-orange-600" /> : <User className="w-5 h-5 text-orange-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{newConversationUser.name || newConversationUser.company_name || 'Użytkownik'}</p>
                          <p className="text-sm text-orange-500">Nowa wiadomość</p>
                        </div>
                      </div>
                    </button>
                  )}
                  {conversations.map((conv) => (
                    <button
                      key={conv.partner_id}
                      onClick={() => selectPartner(conv.partner_id, conv.partner_name, conv.user_type)}
                      className={`w-full p-4 text-left hover:bg-slate-50 transition-colors ${selectedPartner?.id === conv.partner_id && !selectedPartner?.isNew ? 'bg-orange-50' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                          {conv.user_type === 'company' ? <Building2 className="w-5 h-5 text-slate-600" /> : <User className="w-5 h-5 text-slate-600" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900 truncate">{conv.partner_name || 'Użytkownik'}</p>
                          <p className="text-sm text-slate-500 truncate">{conv.last_content}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Messages */}
          <Card className="md:col-span-2 flex flex-col overflow-hidden">
            {selectedPartner ? (
              <>
                <CardHeader className="border-b flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${selectedPartner.isNew ? 'bg-orange-100' : 'bg-slate-200'}`}>
                      {selectedPartner.user_type === 'company' ? <Building2 className={`w-5 h-5 ${selectedPartner.isNew ? 'text-orange-600' : 'text-slate-600'}`} /> : <User className={`w-5 h-5 ${selectedPartner.isNew ? 'text-orange-600' : 'text-slate-600'}`} />}
                    </div>
                    <div>
                      <p className="font-medium">{selectedPartner.name}</p>
                      <p className="text-xs text-slate-500">
                        {selectedPartner.isNew ? 'Nowa konwersacja' : (selectedPartner.user_type === 'company' ? 'Firma' : 'Klient indywidualny')}
                      </p>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
                  {messages.length === 0 && selectedPartner.isNew ? (
                    <div className="text-center text-slate-500 py-8">
                      <MessageSquare className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                      <p className="font-medium">Rozpocznij konwersację</p>
                      <p className="text-sm">Napisz pierwszą wiadomość do {selectedPartner.name}</p>
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center text-slate-500 py-8">
                      <p>Brak wiadomości</p>
                    </div>
                  ) : (
                    messages.map((msg) => (
                      <div key={msg.id || msg.message_id} className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] p-3 rounded-lg ${msg.sender_id === user?.id ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-900'}`}>
                          <p className="text-sm">{msg.content}</p>
                          <p className={`text-xs mt-1 ${msg.sender_id === user?.id ? 'text-orange-100' : 'text-slate-500'}`}>
                            {new Date(msg.created_at).toLocaleString('pl-PL')}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </CardContent>
                <div className="border-t p-4 flex gap-2">
                  <Textarea 
                    value={newMessage} 
                    onChange={(e) => setNewMessage(e.target.value)} 
                    placeholder="Napisz wiadomość..." 
                    rows={2} 
                    className="flex-1" 
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), sendMessage())}
                    data-testid="message-input"
                  />
                  <Button 
                    onClick={sendMessage} 
                    disabled={sending || !newMessage.trim()} 
                    className="bg-orange-500 hover:bg-orange-600"
                    data-testid="send-message-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </>
            ) : (
              <CardContent className="flex-1 flex items-center justify-center text-slate-500">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>Wybierz konwersację</p>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Messages;
